import JavascriptChallenge from "../../javascript_challenge"
import { Exactag } from "../exactag"
import { hasMobileUserAgent } from "../utils"
import "./loginPage.scss"
import { tracking } from "@otto-ec/tracking-bct"
import { querySelectorNonNull } from "../../document"
import { onAllPreloadScriptsLoaded } from "../../utils"
import { AuthField, validateAuthFieldWith } from "../../fragments/authField/authField"
import { trackClickShowHidePasswordToggle } from "../../fragments/passwordInputField/trackClickShowHidePasswordToggle"
import { validateCurrentEmail } from "../../fragments/emailInputField/currentEmailValidator"
import { validateCurrentPassword } from "../../fragments/passwordInputField/currentPasswordValidator"
import { appendSpinnerToFormButtons } from "../../spinner"
import { setBlockedTimer } from "../../timer"
import { validatePostalCode } from "../../fragments/postalCodeInputField/postalCodeValidator"
import { sheetV1 } from "@otto-ec/otto-components/sheet"
import { EVENT_TYPE } from "@otto-ec/otto-components/sheet/v1"

const FORM_SELECTOR = ".fti-ci-js-loginFormContainer"

class LoginPage {
    private readonly emailField: AuthField | undefined
    private readonly passwordField: AuthField
    private readonly postalCodeField: AuthField | undefined
    private readonly loginFlow: string

    constructor(
        document: Document,
        window: Window,
        private readonly exactag = new Exactag(),
    ) {
        if (document.querySelector(".fti-ci-js-login_welcomeBack")) {
            this.loginFlow = "SoftLoggedOut"
        } else {
            this.loginFlow = "Login"
            const emailField: HTMLOcAuthFieldV1Element = querySelectorNonNull(document, ".fti-ci-js-email-field")
            this.emailField = new AuthField(emailField)
            validateAuthFieldWith(this.emailField, validateCurrentEmail)
        }
        const rememberMeCheckbox: HTMLInputElement | null = document.querySelector(".fti-ci-js-rememberMeCheckbox")
        if (rememberMeCheckbox) {
            this.setRememberMeForMobileDevices(window, rememberMeCheckbox).catch((e: unknown) => {
                console.error(e)
            })
        }

        const passwordField: HTMLOcAuthFieldV1Element = querySelectorNonNull(document, ".fti-ci-js-password-field")
        this.passwordField = new AuthField(passwordField)
        validateAuthFieldWith(this.passwordField, validateCurrentPassword)
        trackClickShowHidePasswordToggle({
            trackOn: passwordField,
            showPasswordTrackingEvent: {
                user_LoginActivity: ["click_ShowPassword"],
                user_LoginFlow: [this.loginFlow],
            },
            hidePasswordTrackingEvent: {
                user_LoginActivity: ["click_HidePassword"],
                user_LoginFlow: [this.loginFlow],
            },
        })

        this.addChallenge(document)
        const postalCodeFieldElement = document.querySelector<HTMLOcAuthFieldV1Element>(".fti-ci-js-postalCode-field")
        if (postalCodeFieldElement) {
            this.postalCodeField = new AuthField(postalCodeFieldElement)
            validateAuthFieldWith(this.postalCodeField, validatePostalCode)
        }
        this.exactag.registerExactag()

        setBlockedTimer(document)
        appendSpinnerToFormButtons(document, FORM_SELECTOR)
        const sheet = document.querySelector<HTMLElement>("oc-sheet-v1[id]")
        const closeButton = sheet?.querySelector<HTMLElement>("[data-fti-ci-sheet-close]")
        if (sheet && closeButton) {
            this.initSheetActions(sheet, closeButton)
        }
    }

    private initSheetActions(sheet: HTMLElement, closeButton: HTMLElement) {
        const actionsSlot = closeButton.closest<HTMLElement>("[slot='actions']") ?? closeButton
        closeButton.addEventListener("click", () => {
            sheetV1.close()
        })
        sheet.addEventListener(EVENT_TYPE.CONTENT_APPLY, () => {
            sheet.append(actionsSlot)
        })
    }

    private addChallenge(document: Document) {
        const challengeValue = document.querySelector(".fti-ci-js-challenge-value")?.getAttribute("data-value")
        if (challengeValue) {
            const formElement: HTMLFormElement = querySelectorNonNull(document, FORM_SELECTOR)
            const javascriptChallenge = new JavascriptChallenge(formElement)
            javascriptChallenge.setHiddenChallengeValue(challengeValue)
        }
    }

    private async setRememberMeForMobileDevices(window: Window, rememberMeCheckbox: HTMLInputElement) {
        const isCustomerSelection = rememberMeCheckbox.hasAttribute("data-fti-ci-state-chosen-by-customer")
        if (isCustomerSelection) {
            return
        }
        const isMobileUserAgent = hasMobileUserAgent(window.navigator.userAgent)
        const isLandscape = window.matchMedia("(orientation: landscape)").matches || false
        const isPortrait = window.matchMedia("(orientation: portrait)").matches || false
        const isMobileSize = (isPortrait && window.innerWidth < 768) || (isLandscape && window.innerWidth < 1024)
        const isTouchDevice = window.navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches || false
        const isMobileDevice = isMobileUserAgent || (isMobileSize && isTouchDevice)
        if (isMobileDevice) {
            rememberMeCheckbox.checked = isMobileDevice
        }
        await tracking.submitMerge({
            user_LoginRememberMePreselected: [isMobileDevice.toString()],
        })
    }
}

onAllPreloadScriptsLoaded(() => new LoginPage(document, window))

export default LoginPage

